import React, { useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary,
    Box, Button, Card, CardContent, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControl, FormControlLabel, InputLabel,
    MenuItem, Select, Switch, TextField, Typography,
    ToggleButton, ToggleButtonGroup, Autocomplete
} from '@mui/material';
import { Add, Delete, Edit, ExpandMore, OpenInNew } from '@mui/icons-material';
import { useEstacoesKDS } from '../../hooks/useEstacoesKDS';
import { useConfiguracaoKDS } from '../../hooks/useConfiguracaoKDS';
import { IConfiguracaoEstacaoSimplesKDS, IEstacaoKDS, ModoDisparo, TipoEstacaoKDS, TipoKDS } from '../../interfaces/IKDS';
import { useNavigate } from 'react-router-dom';
import { useFetchCategorias } from '../../../produtos/hooks/useFetchCategorias';

const TipoEstacaoLabel: Record<TipoEstacaoKDS, string> = {
    [TipoEstacaoKDS.PREPARO]: 'Preparo',
    [TipoEstacaoKDS.EXPEDICAO]: 'Expedição',
    [TipoEstacaoKDS.BEBIDAS]: 'Bebidas',
    [TipoEstacaoKDS.SOBREMESAS]: 'Sobremesas',
    [TipoEstacaoKDS.CUSTOMIZADO]: 'Customizado',
};

const estadoInicial: Partial<IEstacaoKDS> = {
    nome: '',
    tipo: TipoEstacaoKDS.PREPARO,
    cor: '#2196F3',
    icone: 'kitchen',
    configuracaoExibicao: {
        tempoAlertaAmareloSegundos: 300,
        tempoAlertaVermelhoSegundos: 600,
        temaEscuro: true,
        mostrarTempoCorrido: true,
        mostrarTempoEstimado: true,
    } as any,
};

export const ConfiguracaoCozinha: React.FC = () => {
    const { estacoes, loading, salvar, excluir } = useEstacoesKDS();
    const { configuracao, salvarModoDisparo, salvarTipoKDS, kdsDesabilitado } = useConfiguracaoKDS();
    const navigate = useNavigate();
    const [dialogoAberto, setDialogoAberto] = useState(false);
    const [form, setForm] = useState<Partial<IEstacaoKDS>>(estadoInicial);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    const { categorias: categoriasBruto } = useFetchCategorias();
    const categorias = categoriasBruto ?? [];

    const abrirDialog = (estacao?: IEstacaoKDS) => {
        setForm(estacao ? { ...estacao } : { ...estadoInicial });
        setErro('');
        setDialogoAberto(true);
    };

    const handleSalvar = async () => {
        if (!form.nome?.trim()) { setErro('Nome é obrigatório'); return; }
        setSalvando(true);
        try {
            await salvar(form);
            setDialogoAberto(false);
        } catch {
            setErro('Erro ao salvar. Tente novamente.');
        } finally {
            setSalvando(false);
        }
    };

    const handleExcluir = async (uuid: string) => {
        if (!window.confirm('Remover esta estação?')) return;
        await excluir(uuid);
    };

    const atualizarConfig = (campo: string, valor: any) => {
        setForm(f => ({
            ...f,
            configuracaoExibicao: { ...f.configuracaoExibicao as any, [campo]: valor }
        }));
    };

    const getConfiguracaoSimples = (estacaoUuid: string): IConfiguracaoEstacaoSimplesKDS => {
        return configuracao.configuracoesEstacaoSimples?.find(c => c.estacaoKdsUuid === estacaoUuid)
            ?? { estacaoKdsUuid: estacaoUuid, categoriaUuids: [], produtoUuids: [] };
    };

    const atualizarConfiguracaoSimples = async (estacaoUuid: string, dados: Partial<IConfiguracaoEstacaoSimplesKDS>) => {
        const atual = getConfiguracaoSimples(estacaoUuid);
        const nova = { ...atual, ...dados };
        const novas = [
            ...(configuracao.configuracoesEstacaoSimples ?? []).filter(c => c.estacaoKdsUuid !== estacaoUuid),
            nova,
        ];
        await salvarTipoKDS('simples', novas);
    };

    const todosProdutos = categorias.flatMap(cat =>
        (cat.produtos ?? []).map(p => ({ uuid: p.uuid, descricao: `${p.descricao} (${cat.descricao})`, categoriaUuid: cat.uuid ?? '' }))
    );

    return (
        <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">Estações KDS</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure as estações de cozinha e acesse os painéis de display
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={() => navigate('/cozinha/expedicao')}>
                        Expedição
                    </Button>
                    <Button variant="outlined" startIcon={<OpenInNew />} onClick={() => window.open('/cozinha/expedicao', '_blank')}>
                        Expedição (nova aba)
                    </Button>
                    <Button variant="contained" startIcon={<Add />} onClick={() => abrirDialog()}>
                        Nova Estação
                    </Button>
                </Box>
            </Box>

            {loading && <CircularProgress />}

            {!loading && estacoes.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                    <Typography variant="h6">Nenhuma estação configurada</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Crie estações para organizar sua cozinha (Ex: Grill, Frios, Bebidas)
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {estacoes.map(estacao => (
                    <Card key={estacao.uuid} sx={{ border: `2px solid ${estacao.cor}`, borderRadius: 2 }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: estacao.cor, flexShrink: 0 }} />
                                <Box>
                                    <Typography fontWeight="bold">{estacao.nome}</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                        <Chip label={TipoEstacaoLabel[estacao.tipo]} size="small" />
                                        <Chip
                                            label={`Alerta: ${estacao.configuracaoExibicao?.tempoAlertaAmareloSegundos ?? 300}s`}
                                            size="small"
                                            sx={{ background: '#ff9800', color: 'white' }}
                                        />
                                        {estacao.modoExpedicaoArea && (
                                            <Chip label="Expedição por área" size="small" color="secondary" />
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => navigate(`/cozinha/painel/${estacao.uuid}`)}
                                >
                                    Abrir Painel
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNew />}
                                    onClick={() => window.open(`/cozinha/painel/${estacao.uuid}`, '_blank')}
                                >
                                    Nova aba
                                </Button>
                                <Button size="small" startIcon={<Edit />} onClick={() => abrirDialog(estacao)}>
                                    Editar
                                </Button>
                                <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleExcluir(estacao.uuid)}>
                                    Remover
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Modo KDS</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Define como o sistema KDS processa os pedidos.
                </Typography>
                <ToggleButtonGroup
                    value={configuracao.tipoKDS ?? 'completo'}
                    exclusive
                    onChange={(_, value) => value && salvarTipoKDS(value as TipoKDS)}
                    size="small"
                >
                    <ToggleButton value="completo">
                        <Box sx={{ textAlign: 'left', textTransform: 'none' }}>
                            <Typography variant="body2" fontWeight="bold">Completo</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Roteamento configurado por produto/processo
                            </Typography>
                        </Box>
                    </ToggleButton>
                    <ToggleButton value="simples">
                        <Box sx={{ textAlign: 'left', textTransform: 'none' }}>
                            <Typography variant="body2" fontWeight="bold">Simples</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Roteamento configurado por estação (categoria/produto)
                            </Typography>
                        </Box>
                    </ToggleButton>
                    <ToggleButton value="desabilitado">
                        <Box sx={{ textAlign: 'left', textTransform: 'none' }}>
                            <Typography variant="body2" fontWeight="bold">Desabilitado</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Pedidos não são enviados ao KDS
                            </Typography>
                        </Box>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {configuracao.tipoKDS === 'simples' && estacoes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Roteamento por Estação
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Configure quais categorias e produtos aparecem em cada estação.
                    </Typography>
                    {estacoes.map(estacao => {
                        const cfg = getConfiguracaoSimples(estacao.uuid);
                        const categoriasSelecionadas = categorias.filter(c => c.uuid && cfg.categoriaUuids.includes(c.uuid));
                        const produtosSelecionados = todosProdutos.filter(p => cfg.produtoUuids.includes(p.uuid));
                        return (
                            <Accordion key={estacao.uuid} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: estacao.cor }} />
                                        <Typography fontWeight="bold">{estacao.nome}</Typography>
                                        <Chip
                                            label={`${cfg.categoriaUuids.length} cat. / ${cfg.produtoUuids.length} prod.`}
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={categorias}
                                            getOptionLabel={c => c.descricao}
                                            value={categoriasSelecionadas}
                                            onChange={(_, value) => atualizarConfiguracaoSimples(estacao.uuid, { categoriaUuids: value.map(c => c.uuid ?? '') })}
                                            renderInput={params => <TextField {...params} label="Categorias" />}
                                            isOptionEqualToValue={(opt, val) => opt.uuid === val.uuid}
                                        />
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={todosProdutos}
                                            getOptionLabel={p => p.descricao}
                                            value={produtosSelecionados}
                                            onChange={(_, value) => atualizarConfiguracaoSimples(estacao.uuid, { produtoUuids: value.map(p => p.uuid) })}
                                            renderInput={params => <TextField {...params} label="Produtos específicos" />}
                                            isOptionEqualToValue={(opt, val) => opt.uuid === val.uuid}
                                        />
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            )}

            {!kdsDesabilitado && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>Disparo de Pedidos</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Define quando os itens de um pedido aparecem nas estações de cozinha.
                        </Typography>
                        <ToggleButtonGroup
                            value={configuracao.modoDisparo}
                            exclusive
                            onChange={(_, value) => value && salvarModoDisparo(value as ModoDisparo)}
                            size="small"
                        >
                            <ToggleButton value="automatico">
                                <Box sx={{ textAlign: 'left', textTransform: 'none' }}>
                                    <Typography variant="body2" fontWeight="bold">Automático</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Sistema calcula e dispara cada item no momento certo
                                    </Typography>
                                </Box>
                            </ToggleButton>
                            <ToggleButton value="manual">
                                <Box sx={{ textAlign: 'left', textTransform: 'none' }}>
                                    <Typography variant="body2" fontWeight="bold">Manual</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Expeditor decide quando disparar o pedido
                                    </Typography>
                                </Box>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </>
            )}

            <Dialog open={dialogoAberto} onClose={() => setDialogoAberto(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{form.uuid ? 'Editar Estação KDS' : 'Nova Estação KDS'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <TextField
                        label="Nome da estação *"
                        value={form.nome ?? ''}
                        onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                        error={!!erro}
                        helperText={erro}
                        fullWidth
                        size="small"
                    />
                    <FormControl fullWidth size="small">
                        <InputLabel>Tipo</InputLabel>
                        <Select
                            label="Tipo"
                            value={form.tipo ?? TipoEstacaoKDS.PREPARO}
                            onChange={e => setForm(f => ({ ...f, tipo: e.target.value as TipoEstacaoKDS }))}
                        >
                            {Object.entries(TipoEstacaoLabel).map(([value, label]) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            label="Cor (hex)"
                            value={form.cor ?? '#2196F3'}
                            onChange={e => setForm(f => ({ ...f, cor: e.target.value }))}
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <Box sx={{ width: 40, height: 40, borderRadius: 1, background: form.cor ?? '#2196F3', border: '1px solid rgba(0,0,0,0.2)', flexShrink: 0 }} />
                    </Box>
                    <Divider />
                    <Typography variant="subtitle2" fontWeight="bold">Alertas de Tempo</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Alerta amarelo (seg)"
                            type="number"
                            size="small"
                            value={(form.configuracaoExibicao as any)?.tempoAlertaAmareloSegundos ?? 300}
                            onChange={e => atualizarConfig('tempoAlertaAmareloSegundos', Number(e.target.value))}
                            fullWidth
                        />
                        <TextField
                            label="Alerta vermelho (seg)"
                            type="number"
                            size="small"
                            value={(form.configuracaoExibicao as any)?.tempoAlertaVermelhoSegundos ?? 600}
                            onChange={e => atualizarConfig('tempoAlertaVermelhoSegundos', Number(e.target.value))}
                            fullWidth
                        />
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={(form.configuracaoExibicao as any)?.temaEscuro ?? true}
                                onChange={e => atualizarConfig('temaEscuro', e.target.checked)}
                            />
                        }
                        label="Tema escuro no painel"
                    />
                    <Divider />
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.modoExpedicaoArea ?? false}
                                    onChange={e => setForm(f => ({ ...f, modoExpedicaoArea: e.target.checked }))}
                                />
                            }
                            label="Expedição por área"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                            {form.modoExpedicaoArea
                                ? 'O painel exibe todos os itens dos pedidos que passam por esta estação, com controles de disparo e liberação'
                                : 'O painel exibe apenas os itens atribuídos a esta estação'}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogoAberto(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSalvar} disabled={salvando}>
                        {salvando ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
